import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, Card, CardContent,
  Chip, Alert, IconButton, CircularProgress, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle,
  DialogContent, DialogActions, Avatar, Tooltip, Divider
} from '@mui/material';
import {
  Person, Search, Refresh as RefreshIcon, Visibility as ViewIcon,
  Phone, Email, ContactPage, Home
} from '@mui/icons-material';
import { getSellers } from '../../services/purchase';
import { hasReadPermission } from '../../utils/menuFilter';
import { useHistory } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';
import { exportAsExcel, exportAsCSV, exportAsPDF } from '../../utils/importExport';
import ExportButton from '../../components/ExportButton/ExportButton';
import AlertModal from '../../components/AlertModal';

const SellerList = () => {
  const history = useHistory();
  const canReadSellers = hasReadPermission('purchase.purchase') || hasReadPermission('purchase');

  const [sellersList, setSellersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selectedSeller, setSelectedSeller] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');

  const showAlert = (message, title = 'Alert') => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertOpen(true);
  };

  const loadSellersData = async (pageNum = 0, rows = 10) => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: pageNum + 1,
        limit: rows,
        search: searchTerm
      };
      const response = await getSellers(params);
      
      const sellersData = response.data || [];
      const pagination = response.pagination || {};
      const total = response.totalRecords || pagination.totalRecords || sellersData.length;

      setSellersList(sellersData);
      setTotalRecords(total);
    } catch (err) {
      console.error('Error loading sellers:', err);
      setError(err.message || 'Failed to load sellers');
      showAlert(err.message || 'Failed to load sellers', 'Error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canReadSellers) {
      loadSellersData(page, rowsPerPage);
    }
  }, [page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    loadSellersData(0, rowsPerPage);
  };

  const handleReset = () => {
    setSearchTerm('');
    setPage(0);
    // Since state update is async, we call loadSellersData directly with cleared search
    setLoading(true);
    const params = {
      page: 1,
      limit: rowsPerPage,
      search: ''
    };
    getSellers(params)
      .then(response => {
        const sellersData = response.data || [];
        const pagination = response.pagination || {};
        const total = response.totalRecords || pagination.totalRecords || sellersData.length;
        setSellersList(sellersData);
        setTotalRecords(total);
      })
      .catch(err => {
        setError(err.message || 'Failed to load sellers');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleOpenViewDialog = (seller) => {
    setSelectedSeller(seller);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedSeller(null);
  };

  const buildExportRows = (list) => {
    return list.map((record, index) => {
      const sellerName = record.farmerName || record.name || 'N/A';
      const fatherName = record.fatherHusbandName || 'N/A';
      const mobileNum = record.mobile || record.mobileNumber || 'N/A';
      const altMobile = record.alternateMobile || 'N/A';
      const emailVal = record.email || 'N/A';
      const aadhaarVal = record.aadhaar || 'N/A';
      const panVal = record.panNumber || record.pan || 'N/A';
      return {
        'S.No': index + 1,
        'Seller Name': sellerName,
        'Father/Husband Name': fatherName,
        'Mobile': mobileNum,
        'Alternate Mobile': altMobile,
        'Email': emailVal,
        'Aadhaar': aadhaarVal,
        'PAN': panVal,
        'Address': `${record.village || ''}, ${record.tehsil || ''}, ${record.district || ''}, ${record.state || ''} - ${record.pincode || ''}`
      };
    });
  };

  const handleExportPdf = () => {
    if (!sellersList.length) {
      showAlert('No seller data available to export.');
      return;
    }
    const rows = buildExportRows(sellersList);
    exportAsPDF({ rows, title: 'Seller List', fileNameBase: 'Seller_List_Export' });
  };

  const handleExportCsv = () => {
    if (!sellersList.length) {
      showAlert('No seller data available to export.');
      return;
    }
    const rows = buildExportRows(sellersList);
    exportAsCSV({ rows, fileNameBase: 'Seller_List_Export' });
  };

  const handleExportExcel = () => {
    if (!sellersList.length) {
      showAlert('No seller data available to export.');
      return;
    }
    const rows = buildExportRows(sellersList);
    exportAsExcel({ rows, sheetName: 'Seller List', fileNameBase: 'Seller_List_Export' });
  };

  if (!canReadSellers) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh', p: 3 }}>
        <Paper elevation={3} sx={{ p: { xs: 3, md: 6 }, textAlign: 'center', maxWidth: 500, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <WarningIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Seller List. Please contact your administrator for access.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              px: 4, py: 1.2, borderRadius: 2, fontWeight: 600, textTransform: 'none'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'start', sm: 'center' }, mb: 4, gap: 2 }}>
        <Typography variant="h4" fontWeight="bold" color="primary.main">
          Seller Directory
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center', flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <ExportButton
            onExportPdf={handleExportPdf}
            onExportCsv={handleExportCsv}
            onExportExcel={handleExportExcel}
            variant="outlined"
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => loadSellersData(page, rowsPerPage)}
            sx={{ px: 2, borderRadius: 2, height: 40, textTransform: 'none', fontWeight: 600 }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                placeholder="Search by Seller Name, Mobile, Aadhaar, PAN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, height: '40px' }}
                >
                  Search
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, height: '40px' }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8f9fa', fontWeight: 'bold' } }}>
                    <TableCell>S.No</TableCell>
                    <TableCell>Seller Name</TableCell>
                    <TableCell>Mobile</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Aadhaar / PAN</TableCell>
                    <TableCell>Village/City</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sellersList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="text.secondary">No seller records found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    sellersList.map((seller, index) => {
                      const sellerName = seller.farmerName || seller.name || 'N/A';
                      const fatherName = seller.fatherHusbandName || '';
                      const mobileNum = seller.mobile || seller.mobileNumber || 'N/A';
                      const emailVal = seller.email || 'N/A';
                      const aadhaarVal = seller.aadhaar || '';
                      const panVal = seller.panNumber || seller.pan || '';
                      const villageVal = seller.village || 'N/A';
                      const locationVal = seller.district ? `${seller.district}, ${seller.state || ''}` : '';
                      return (
                        <TableRow key={seller._id || index} hover>
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32, fontSize: '0.9rem' }}>
                                {sellerName?.[0]?.toUpperCase() || <Person />}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {sellerName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {fatherName ? `S/O, W/O: ${fatherName}` : ''}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{mobileNum}</TableCell>
                          <TableCell>{emailVal}</TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {aadhaarVal ? `AADHAAR: ${aadhaarVal}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {panVal ? `PAN: ${panVal}` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{villageVal}</Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {locationVal}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenViewDialog(seller)}
                                color="primary"
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person /> Seller Profile
        </DialogTitle>
        <DialogContent dividers>
          {selectedSeller && (() => {
            const sellerName = selectedSeller.farmerName || selectedSeller.name || 'N/A';
            const fatherName = selectedSeller.fatherHusbandName || '';
            const mobileNum = selectedSeller.mobile || selectedSeller.mobileNumber || 'N/A';
            const altMobile = selectedSeller.alternateMobile || '-';
            const emailVal = selectedSeller.email || 'N/A';
            const aadhaarVal = selectedSeller.aadhaar || 'N/A';
            const panVal = selectedSeller.panNumber || selectedSeller.pan || 'N/A';
            const villageVal = selectedSeller.village || 'N/A';
            const tehsilVal = selectedSeller.tehsil || 'N/A';
            const districtVal = selectedSeller.district || 'N/A';
            const stateVal = selectedSeller.state || 'N/A';
            const pincodeVal = selectedSeller.pincode || 'N/A';
            return (
              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid item xs={12} display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                  <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.8rem' }}>
                    {sellerName?.[0]?.toUpperCase() || <Person />}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">{sellerName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {fatherName ? `Father/Husband: ${fatherName}` : ''}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}><Divider><Chip label="Contact Details" /></Divider></Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <Phone fontSize="inherit" /> Mobile Number
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">{mobileNum}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <Phone fontSize="inherit" /> Alternate Mobile
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">{altMobile}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <Email fontSize="inherit" /> Email Address
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">{emailVal}</Typography>
                </Grid>

                <Grid item xs={12}><Divider><Chip label="Government Identity" /></Divider></Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <ContactPage fontSize="inherit" /> Aadhaar Number
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">{aadhaarVal}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <ContactPage fontSize="inherit" /> PAN Card
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">{panVal}</Typography>
                </Grid>

                <Grid item xs={12}><Divider><Chip label="Address Information" /></Divider></Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <Home fontSize="inherit" /> Address / Village
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">{villageVal}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                    <Home fontSize="inherit" /> Tehsil
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">{tehsilVal}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">District</Typography>
                  <Typography variant="body1" fontWeight="medium">{districtVal}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">State</Typography>
                  <Typography variant="body1" fontWeight="medium">{stateVal}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Pincode</Typography>
                  <Typography variant="body1" fontWeight="medium">{pincodeVal}</Typography>
                </Grid>
              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>

      <AlertModal
        open={alertOpen}
        title={alertTitle}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default SellerList;
